import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareLock, Unlink } from "lucide-react";
import { API } from "../../context/AuthContext";

function formatUS(e164) {
  const m = (e164 || "").match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

export default function SmsPanel() {
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sms/status`).then((res) => setStatus(res.data)).catch(() => {});
  }, []);

  const link = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/sms/link`, { phone });
      setStatus((s) => ({ ...s, linked_phone: res.data.linked_phone }));
      setPhone("");
      toast.success("Phone linked! Text evidence to your Flux number anytime.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't link that number.");
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    try {
      await axios.delete(`${API}/sms/link`);
      setStatus((s) => ({ ...s, linked_phone: "" }));
      toast.success("Phone unlinked.");
    } catch {
      toast.error("Couldn't unlink.");
    }
  };

  if (!status) return null;

  return (
    <div className="bg-[#131f3a] border border-white/5 p-5 rounded-2xl shadow-xl space-y-3" data-testid="sms-panel">
      <div className="flex items-center gap-2">
        <MessageSquareLock className="w-4 h-4 text-[#2dd4bf]" />
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-300">SMS Evidence Stash</h4>
      </div>
      {status.linked_phone ? (
        <>
          <p className="text-[11px] text-gray-400 leading-relaxed">
            Text notes or photos from <span className="text-white font-mono">{formatUS(status.linked_phone)}</span> to:
          </p>
          <a href={`sms:${status.twilio_number}`} className="block text-center bg-gray-950 border border-[#2dd4bf]/30 rounded-xl py-3 font-mono font-bold text-[#2dd4bf] text-sm tracking-wider" data-testid="sms-twilio-number">
            {formatUS(status.twilio_number)}
          </a>
          <p className="text-[10px] text-gray-500 leading-relaxed">
            Every text lands on your timeline instantly — no app, no login, no trace on your phone beyond a normal text. Delete the thread after sending.
          </p>
          <button onClick={unlink} data-testid="sms-unlink-btn" className="flex items-center gap-1.5 text-[10px] font-mono text-gray-500 hover:text-rose-400 transition-colors duration-200">
            <Unlink className="w-3 h-3" /> UNLINK PHONE
          </button>
        </>
      ) : (
        <>
          <p className="text-[11px] text-gray-400 leading-relaxed">
            Link your phone, then text evidence to <span className="text-[#2dd4bf] font-mono">{formatUS(status.twilio_number)}</span> — it lands straight on your timeline. Built for stashing evidence discreetly.
          </p>
          <form onSubmit={link} className="flex gap-2">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              data-testid="sms-phone-input"
              className="flex-1 bg-gray-950 border border-white/10 rounded-lg px-3 py-2 text-xs text-gray-100 focus:border-[#2dd4bf]/50 focus:outline-none"
            />
            <button type="submit" disabled={busy} data-testid="sms-link-btn" className="bg-[#2dd4bf] hover:bg-[#5eead4] text-gray-950 font-bold text-[10px] font-mono px-3 py-2 rounded-lg transition-colors duration-200 disabled:opacity-50">
              {busy ? "..." : "LINK"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
